import { useState } from "react";

type ReasonModalProps = {
  open: boolean;
  title: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  confirmLabel?: string;
  confirmTone?: "danger" | "primary";
  submitting?: boolean;
  /** Shown inside the modal (e.g. a failed submit) — without this, errors set in state after
   *  onConfirm rejects would render behind the modal's full-screen overlay and never be seen. */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

/** Small in-app modal for "confirm + optionally explain why" actions (cancel, reject, approve
 *  with notes, etc.) — replaces native window.prompt() dialogs, which look and feel like
 *  browser chrome rather than part of the app. Shared across modules; not tied to any one
 *  feature. */
export function ReasonModal({
  open,
  title,
  label = "Reason",
  placeholder,
  required = false,
  confirmLabel = "Confirm",
  confirmTone = "primary",
  submitting = false,
  error,
  onCancel,
  onConfirm,
}: ReasonModalProps) {
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);

  if (!open) return null;

  const showError = touched && required && !value.trim();

  function handleConfirm() {
    if (required && !value.trim()) {
      setTouched(true);
      return;
    }
    onConfirm(value.trim());
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-bold text-slate-900">{title}</h2>

        <div className="mt-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            placeholder={placeholder}
            className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          {showError && <p className="mt-1 text-xs text-red-500">This field is required.</p>}
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              confirmTone === "danger" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-700 hover:bg-emerald-800"
            }`}
          >
            {submitting ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
